<template>
  <div class="table-wrapper">
    <div class="table-header">
      <div class="table-header__title">
        <p class="text">
          CDN compares a request for an object with the path patterns in your
          cache behaviors based on the order of the cache behaviors in your
          distribution. Arrange cache behaviors in the order in which you want
          CDN to evaluate them.
        </p>
      </div>
      <div class="d-md-flex">
        <div class="d-flex mb-1 mb-lg-0">
          <b-button
            v-if="$can('create', 'behaviors', 'cdn')"
            variant="bg-primary"
            @click="onCreateClick()"
          >
            <GjIcon
              class="icon-left"
              name="Plus"
            />
            Create Behavior
          </b-button>
          <b-button
            v-if="$can('update', 'behaviors', 'cdn')"
            variant="bg-white"
            :disabled="!isSelected"
            class="d-none d-lg-block"
            @click="onEditClick"
          >
            Edit
          </b-button>
          <button-loading
            v-if="$can('delete', 'behaviors', 'cdn')"
            variant="bg-white"
            :disabled="!isDeletable"
            :spinning="removingBehavior"
            class="d-none d-lg-block"
            @click="onDeleteClick"
          >
            Delete
          </button-loading>
        </div>
        <div
          v-if="$can('update', 'behaviors', 'cdn')"
          class="pt-1 pt-md-0 d-none d-lg-block"
        >
          <span class="text mr-1">Change Precendence</span>
          <b-button
            variant="bg-white"
            :disabled="!canMoveUp"
            @click="onMoveUp"
          >
            Move up
          </b-button>
          <b-button
            variant="bg-white"
            :disabled="!canMoveDown"
            @click="onMoveDown"
          >
            Move down
          </b-button>
          <button-loading
            v-if="precedenceChanged"
            variant="bg-primary"
            :spinning="updatingBehavior"
            @click="onChangePrecedence()"
          >
            Save
          </button-loading>
        </div>

        <b-dropdown
          v-if="$can('get', 'behaviors', 'cdn')"
          id="dropdown-2"
          variant="bg-white"
          class="dropdown-icon-wrapper ml-auto"
        >
          <template #button-content>
            <span>{{ pageSize }}</span>
            <GjIcon
              class="icon-right"
              name="ArrowDown"
            />
          </template>
          <b-dropdown-item @click="behaviorsSize = 20">
            20
          </b-dropdown-item>
          <b-dropdown-item @click="behaviorsSize = 50">
            50
          </b-dropdown-item>
          <b-dropdown-item @click="behaviorsSize = 100">
            100
          </b-dropdown-item>
          <b-dropdown-item @click="behaviorsSize = 200">
            200
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="behaviorsTable"
          :items="behaviors"
          :fields="fields"
          :class="{ loaded: !loading }"
          sort-by="precedence"
          select-mode="single"
          show-empty
          selectable
          responsive
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <template v-else>
              <no-data
                no-data-text="No behaviors have been added yet..."
                create-text="+ Create Behavior"
                access-page="Behaviors"
                :has-permissions="hasPermissions"
                @action="onCreateClick()"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="loading && behaviors.length > 0" />
      </div>
      <div class="position-relative d-lg-none mt-2">
        <div :class="{ 'opacity-7': loading }">
          <template v-for="behavior in behaviors">
            <card
              :key="behavior.id"
              :fields="fields"
              :item="behavior"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                  @show="select(behavior)"
                  @hide="select(null)"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item
                    v-if="$can('update', 'behaviors', 'cdn')"
                    @click="onEditClick"
                  >
                    Edit
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'behaviors', 'cdn')"
                    :disabled="!isDeletable"
                    @click="onDeleteClick"
                  >Delete</b-dropdown-item>
                  <template v-if="$can('update', 'behaviors', 'cdn')">
                    <b-dropdown-divider />
                    <b-dropdown-header id="dropdown-header-2">
                      Change Precendence
                    </b-dropdown-header>
                    <b-dropdown-item
                      :disabled="!canMoveUp"
                      aria-describedby="dropdown-header-2"
                      @click="onMobileMoveUp"
                    >
                      Move up
                    </b-dropdown-item>
                    <b-dropdown-item
                      :disabled="!canMoveDown"
                      aria-describedby="dropdown-header-2"
                      @click="onMobileMoveDown"
                    >
                      Move down
                    </b-dropdown-item>
                  </template>
                </b-dropdown>
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && behaviors.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No behaviors have been added yet..."
              create-text="+ Create Behavior"
              access-page="Behaviors"
              :has-permissions="hasPermissions"
              @action="onCreateClick()"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="behaviors.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'Behaviors',
  components: {
    Card,
  },
  data() {
    return {
      selected: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'id',
          label: 'id',
          variant: 'select',
          isPrimary: true,
          hide: true,
          tdClass: 'd-none',
          thClass: 'd-none',
        },
        'precedence',
        'pathPattern',
        { key: 'origin', isPrimary: true },
        'viewerProtocolPolicy',
        'cachePolicyName',
        'originRequestPolicyName',
      ],
      precedenceChanged: false,
      behaviorsSize: 20,
      hasPermissions: true,
    }
  },
  computed: {
    behaviors() {
      return this.$store.state.cdn.behaviors
    },
    resultInfo() {
      return this.$store.state.cdn.behaviorsResultInfo
    },
    removingBehavior() {
      return this.$store.state.cdn.removingBehavior
    },
    updatingBehavior() {
      return this.$store.state.cdn.updatingBehavior
    },
    loading() {
      return this.$store.state.cdn.fetchingBehaviors
    },
    isSelected() {
      return this.selected[0]
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo?.pageSize || this.behaviorsSize
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    isDeletable() {
      return this.selected[0] && this.selected[0].pathPattern !== '*'
    },
    hasData() {
      return this.behaviors[0]
    },
    canMoveUp() {
      return (
        this.selected[0]
        && this.selected[0].precedence !== 0
        && this.selected[0].pathPattern !== '*'
      )
    },
    canMoveDown() {
      return (
        this.selected[0]
        && this.selected[0].precedence < this.lastPrecedence
        && this.selected[0].pathPattern !== '*'
      )
    },
    lastPrecedence() {
      return this.behaviors.length - 2
    },
    sortedItems() {
      return this.$refs.behaviorsTable.sortedItems
    },
    currentPage: {
      get() {
        return this.$store.getters['cdn/behaviorsCurrentPage']
      },
      set(val) {
        this.$store.commit('cdn/SET_BEHAVIORS_CURRENT_PAGE', val)
      },
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  watch: {
    currentPage() {
      this.onGetBehaviors()
    },
    behaviorsSize(val) {
      this.resultInfo.pageSize = val
      if (this.currentPage === 1) {
        this.onGetBehaviors()
      } else {
        this.currentPage = 1
      }
    },
  },
  created() {
    this.id = this.$route.params.id
    if (
      this.$store.state.app.previousRouteName !== 'distribution-behaviors-edit'
    ) {
      this.currentPage = 1
    }
    this.onGetBehaviors()
  },
  methods: {
    select(behavior) {
      this.selected = behavior !== null ? [behavior] : []
    },
    onGetBehaviors() {
      this.$store
        .dispatch('cdn/fetchBehaviors', {
          id: this.id,
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching behaviors.',
          )
        })
    },
    onRowSelected(item) {
      this.selected = item
    },
    onDeleteClick() {
      this.confirmDelete(
        'Delete Behavior',
        'Are you sure you want to delete this behavior?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('cdn/removeBehavior', {
              distributionId: this.id,
              id: this.selected[0].id,
            })
            .then(() => {
              this.successToast('Behavior was removed')
              this.selected = []
              if (this.behaviors.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.onGetBehaviors()
              }
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    onEditClick() {
      if (this.isSelected) {
        this.$router.push({
          name: 'distribution-behaviors-edit',
          params: { id: this.id, behaviorId: this.selected[0].id },
        })
      }
    },
    onCreateClick() {
      this.$router.push({
        name: 'distribution-behaviors-create',
        params: { id: this.id },
      })
    },
    onMoveUp() {
      this.precedenceChanged = true
      const { precedence } = this.selected[0]

      const index = this.behaviors.findIndex(b => b.precedence === precedence)
      const secIndex = this.behaviors.findIndex(
        b => b.precedence === precedence - 1,
      )

      this.behaviors[index].precedence--
      this.behaviors[secIndex].precedence++

      setTimeout(() => {
        const indexToSelect = this.sortedItems.findIndex(
          b => b.precedence === precedence - 1,
        )
        this.$refs.behaviorsTable.selectRow(indexToSelect)
      }, 50)
    },
    onMoveDown() {
      this.precedenceChanged = true
      const { precedence } = this.selected[0]

      const index = this.behaviors.findIndex(b => b.precedence === precedence)
      const secIndex = this.behaviors.findIndex(
        b => b.precedence === precedence + 1,
      )

      this.behaviors[index].precedence++
      this.behaviors[secIndex].precedence--

      setTimeout(() => {
        const indexToSelect = this.sortedItems.findIndex(
          b => b.precedence === precedence + 1,
        )
        this.$refs.behaviorsTable.selectRow(indexToSelect)
      }, 50)
    },
    onChangePrecedence() {
      const newBehaviors = []
      this.behaviors.forEach(element => {
        newBehaviors.push({
          id: element.id,
          precedence: element.precedence,
        })
      })
      this.$store
        .dispatch('cdn/updateBehaviors', {
          distributionId: this.$route.params.id,
          behaviors: newBehaviors,
        })
        .then(() => {
          this.onGetBehaviors()
          this.precedenceChanged = false
          this.successToast('Behavior was updated')
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },
    onMobileMoveUp() {
      this.onMoveUp()
      this.onChangePrecedence()
    },
    onMobileMoveDown() {
      this.onMoveDown()
      this.onChangePrecedence()
    },
  },
}
</script>
<style lang="scss" scoped>
.dropdown-icon-wrapper {
  ::v-deep {
    ul {
      width: calc(100% - 10px);
    }
    .btn {
      margin-right: 0;
    }
  }
}
</style>
